import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import {SaveOutlined} from '@material-ui/icons';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import {CampoData, CampoSelect} from '../../components/processo/CamposProcesso.jsx';
import {FORMATO_DATA_WIRE, dataAteh, dataWireValida, normalizarData} from '../../components/processo/processoSelecao.js';

const dataObrigatoria = () => Yup.string()
    .test('obrigatoria', 'É obrigatória', valor => !!valor && String(valor).trim() !== '')
    .test('valida', 'Data inválida', valor => !valor || dataWireValida(valor, FORMATO_DATA_WIRE));

const FormSchema = Yup.object().shape({
    idTipo: Yup.string().required('É obrigatório'),
    dataInicial: dataObrigatoria(),
    dataFinal: dataObrigatoria()
        .test('posterior', 'Não pode ser anterior à data inicial', function posterior(valor) {
            return dataAteh(this.parent.dataInicial, valor, FORMATO_DATA_WIRE);
        })
});

const VALORES_VAZIOS = {idTipo: '', dataInicial: '', dataFinal: ''};

const paraFormulario = calendario => (calendario ? {
    idTipo: calendario.idTipo === null || calendario.idTipo === undefined ? '' : String(calendario.idTipo),
    dataInicial: normalizarData(calendario.dataInicial, FORMATO_DATA_WIRE),
    dataFinal: normalizarData(calendario.dataFinal, FORMATO_DATA_WIRE)
} : VALORES_VAZIOS);

const useStyles = makeStyles(() => ({
    paper: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    }
}));

/*
 * Calendário do processo de seleção (_formCriarCalendario do PrimeFaces):
 * tipo do calendário e as datas inicial e final (só data), a final não
 * anterior à inicial.
 */
export default function DialogCalendarioProcesso({open, onClose, calendario, tiposCalendario = [], onSalvar}) {
    const classes = useStyles();
    const {control, handleSubmit, reset, formState: {errors, isSubmitting}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_VAZIOS
    });

    const preencher = () => reset(paraFormulario(calendario));

    useEffect(() => {
        if (open) {
            preencher();
        }
    }, [open, calendario]);

    const submit = valores => {
        const tipo = tiposCalendario.find(item => String(item.id) === String(valores.idTipo));
        onSalvar({
            ...(calendario || {}),
            id: calendario?.id ?? null,
            idTipo: Number(valores.idTipo),
            descricaoTipo: tipo?.descricao || calendario?.descricaoTipo || '',
            dataInicial: valores.dataInicial,
            dataFinal: valores.dataFinal
        });
        onClose();
    };

    /* O diálogo fica dentro do form da página na árvore do React: o submit não pode subir até ele. */
    const aoEnviar = evento => {
        evento.stopPropagation();
        handleSubmit(submit)(evento);
    };

    const props = {control, errors};

    return (
        <Dialog open={open} onClose={onClose} TransitionProps={{onEntering: preencher}} scroll='body'
                aria-labelledby='dialog-calendario-processo-titulo' classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-calendario-processo-titulo'>{calendario ? 'Editar Calendário' : 'Adicionar Calendário'}</DialogTitle>
            <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                <form noValidate autoComplete='off' onSubmit={aoEnviar}>
                    <DialogContent classes={{root: classes.contentRoot}}>
                        <Grid container spacing={3}>
                            <CampoSelect {...props} nome='idTipo' rotulo='Tipo do calendário *' sm={12}
                                         opcoes={tiposCalendario.map(item => ({valor: String(item.id), descricao: item.descricao}))}/>
                            <CampoData {...props} nome='dataInicial' rotulo='Data inicial *'/>
                            <CampoData {...props} nome='dataFinal' rotulo='Data final *'/>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <TextButton onClick={onClose}>Fechar</TextButton>
                        <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={isSubmitting}>Salvar</ContainedButton>
                    </DialogActions>
                </form>
            </MuiPickersUtilsProvider>
        </Dialog>
    );
}

DialogCalendarioProcesso.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Calendário em edição ({id, idTipo, descricaoTipo, dataInicial, dataFinal}); nulo ao adicionar. */
    calendario: PropTypes.object,
    tiposCalendario: PropTypes.array,
    onSalvar: PropTypes.func.isRequired
};
